import React from "react";
import { MapPin, Bell, ShieldCheck, Folder, Calendar } from "lucide-react";
import { motion } from "framer-motion";

export default function Features() {
  return (
    <section id="features" className="py-20 sm:py-28 relative bg-white border-t border-slate-50/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#007AFF] mb-3 block">
            POWERFUL TOOLS FOR EVERY DRIVER
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            Core Platform Features
          </h2>
          <p className="text-sm md:text-base text-slate-500 max-w-xl mx-auto font-medium leading-relaxed">
            Everything you need to keep your vehicle running smoothly and efficiently—all in one place.
          </p>
        </motion.div>

        {/* Features Layout */}
        <div className="flex flex-col lg:flex-row gap-6 items-stretch">
          
          {/* Left: SOS Card (1/3) */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="lg:w-[36%] bg-[#FFF5F5] rounded-3xl p-8 md:p-10 flex flex-col justify-between border border-red-100/50 relative overflow-hidden group hover:shadow-[0_12px_40px_rgba(239,68,68,0.12)] transition-all duration-300"
          >
            <div>
              {/* SOS Badge with glowing ambient pulse */}
              <div className="relative w-fit mb-8">
                <div className="absolute inset-0 bg-red-400 rounded-2xl animate-soft-pulse pointer-events-none"></div>
                <div className="w-14 h-14 bg-[#FF4D4D] rounded-2xl flex items-center justify-center text-white font-black text-sm tracking-wider shadow-sm shadow-red-500/30 group-hover:scale-105 transition-transform duration-300 relative z-10">
                  sos
                </div>
              </div>
              
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 mb-4">
                Emergency Roadside SOS
              </h3>
              
              <p className="text-sm text-slate-600 leading-relaxed font-medium mb-8">
                Instant, reliable 24/7 roadside assistance with real-time technician tracking right in your app. Never feel stranded again.
              </p>
            </div>
            
            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-[#FF4D4D] hover:bg-red-600 text-white font-bold py-3.5 rounded-xl transition-colors shadow-sm text-sm"
            >
              Get Help Now
            </motion.button>
          </motion.div>

          {/* Right: 5 Cards Grid (2/3) */}
          <div className="lg:w-[64%] grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {[
              {
                icon: <MapPin className="w-5 h-5 text-cyan-600" />,
                iconBg: "bg-cyan-50 border border-cyan-100/60",
                title: "Automatic Mileage Tracking",
                description: "Precise, hands-free trip logging for maintenance scheduling and reporting.",
                fullWidth: false,
              },
              {
                icon: <Bell className="w-5 h-5 text-amber-600" />,
                iconBg: "bg-amber-50 border border-amber-100/60",
                title: "Intelligent Maintenance Alerts",
                description: "Personalized, timely reminders for scheduled service, oil changes, and recalls.",
                fullWidth: false,
              },
              {
                icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />,
                iconBg: "bg-emerald-50 border border-emerald-100/60",
                title: "Verified Service Network",
                description: "Connect only with verified, top-rated local providers based on authentic community reviews.",
                fullWidth: false,
              },
              {
                icon: <Folder className="w-5 h-5 text-indigo-600" />,
                iconBg: "bg-indigo-50 border border-indigo-100/60",
                title: "Permanent Service History",
                description: "A secure, digital vault for all maintenance records, photos, and receipts, enhancing resale value.",
                fullWidth: false,
              },
              {
                icon: <Calendar className="w-5 h-5 text-[#007AFF]" />,
                iconBg: "bg-blue-50 border border-blue-100/60",
                title: "Effortless Appointment Booking",
                description: "Compare availability and pricing, then book appointments directly through the app in seconds.",
                fullWidth: true,
              },
            ].map((feature, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: 0.1 + (idx * 0.07) }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                key={idx}
                className={`bg-white border border-slate-100 p-6 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:border-slate-200 hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)] transition-all duration-300 flex items-start gap-4 group cursor-default ${
                  feature.fullWidth ? "sm:col-span-2" : ""
                }`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 ${feature.iconBg}`}>
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-slate-900 mb-1 leading-snug group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed font-medium">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>


        </div>
      </div>
    </section>
  );
}

